import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LayoutDashboard, Briefcase, MessageSquare, Heart, CalendarDays, Star, User, Radar, FileText, Trophy, Users, Wrench, MapPin, ShieldCheck, CreditCard, BarChart3, PoundSterling, Layers, Tag, Crown, FileEdit, HelpCircle, Newspaper, AlertTriangle, ScrollText, Megaphone, Send, MessageSquareWarning, PauseCircle, Gift, BellRing, Building2 } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PublicLayout, DashLayout, Protected } from "./components/Layout";
import { PaymentModePrompt } from "./components/PaymentMode";
import Home from "./pages/Home";
import SearchPage from "./pages/Search";
import ProviderProfile from "./pages/ProviderProfile";
import PostJob from "./pages/PostJob";
import Messages from "./pages/Messages";
import Account from "./pages/Account";
import About from "./pages/About";
import { AdvertsBrowse, AdvertDetail, ProAdverts } from "./pages/Adverts";
import UrgentJobs from "./pages/UrgentJobs";
import SearchJobs from "./pages/SearchJobs";
import { Login, Register, Forgot, Reset, VerifyEmail, PaymentResult, AcceptInvite, SecureAccount } from "./pages/Auth";
import AdminInvites from "./pages/AdminInvites";
import ProWelcome from "./pages/ProWelcome";
import LeaveReview from "./pages/LeaveReview";
import CategoryLanding from "./pages/CategoryLanding";
import { CmsPage, HowItWorks, Pricing, Faq, Contact, Services, Advice, Article, LocalSeo } from "./pages/Content";
import { HomeOverview, HomeJobs, HomeJobDetail, HomeSaved, HomeBookings, HomeReviews } from "./pages/Homeowner";
import { ProOverview, ProFindJobs, ProJob, ProLeads, ProMyJobs, ProCustomers, ProReviews, ProBilling, ProAnalytics, ProProfileEditor } from "./pages/Provider";
import { AdminOverview, AdminUsers, AdminProviders, AdminJobs, AdminFinance, AdminCases, AdminAudit } from "./pages/Admin";
import { AdminSettings, AdminPlans, AdminPromotions, AdminPages, AdminFaqs, AdminArticles, AdminCollection } from "./pages/AdminConfig";
import AdminCategoriesPage from "./pages/AdminCategoriesPage";
import { TermsGate } from "./components/TermsGate";
import ComplaintForm from "./pages/ComplaintForm";
import { MyComplaints, ComplaintView } from "./pages/Complaints";
import { AdminComplaints, AdminComplaintDetail } from "./pages/AdminComplaints";
import AdminPausedPros from "./pages/AdminPausedPros";
import { GiftVouchers, GiftVoucherSuccess, AdminVouchers } from "./pages/GiftVouchers";
import { ProVerificationPage, AdminVerification } from "./pages/Verification";
import GuaranteePage from "./pages/Guarantee";
import { PriceGuideIndex, PriceGuidePage, AdminPriceGuides } from "./pages/PriceGuides";
import { HomeReminders } from "./components/Maintenance";
import { HomeProperties, HomePropertyDetail, TenantReport } from "./pages/Properties";

const HO_NAV = [["/dashboard", "Dashboard", LayoutDashboard, true], ["/dashboard/jobs", "My Jobs", Briefcase], ["/dashboard/messages", "Messages", MessageSquare], ["/dashboard/saved", "Saved", Heart],
  ["/dashboard/bookings", "Bookings & Payments", CalendarDays], ["/dashboard/reminders", "Reminders", BellRing], ["/dashboard/properties", "Properties", Building2], ["/dashboard/reviews", "Reviews", Star], ["/dashboard/complaints", "Complaints", MessageSquareWarning], ["/dashboard/account", "Account", User]];
const PRO_NAV = [["/provider", "Dashboard", LayoutDashboard, true], ["/provider/find-jobs", "Find Jobs", Radar], ["/provider/applications", "My Leads", FileText], ["/provider/adverts", "My Adverts", Megaphone], ["/provider/messages", "Messages", MessageSquare],
  ["/provider/my-jobs", "Jobs", Trophy], ["/provider/calendar", "Calendar", CalendarDays], ["/provider/customers", "Customers", Users], ["/provider/profile", "Profile & Services", Wrench],
  ["/provider/verification", "Verification", ShieldCheck], ["/provider/reviews", "Reviews", Star], ["/provider/billing", "Billing", CreditCard], ["/provider/analytics", "Analytics", BarChart3], ["/provider/complaints", "Complaints", MessageSquareWarning], ["/provider/account", "Account", User]];
const ADMIN_NAV = [["/admin", "Dashboard", LayoutDashboard, true, "overview"], ["/admin/users", "Users", Users, false, "users"], ["/admin/providers", "Providers", Wrench, false, "providers"], ["/admin/verification", "Verification", ShieldCheck, false, "verification"], ["/admin/invites", "Invite Providers", Send, false, "providers"], ["/admin/jobs", "Jobs", Briefcase, false, "jobs"], ["/admin/adverts", "Service Adverts", Megaphone, false, "providers"],
  ["/admin/pricing", "Revenue & Pricing", PoundSterling, false, "pricing"], ["/admin/plans", "Plans", Crown, false, "pricing"], ["/admin/promotions", "Promotions", Tag, false, "pricing"], ["/admin/finance", "Finance", CreditCard, false, "transactions"], ["/admin/vouchers", "Gift Vouchers", Gift, false, "finance"], ["/admin/price-guides", "Price Guides", PoundSterling, false, "cms"],
  ["/admin/categories", "Categories", Layers, false, "categories"], ["/admin/pages", "CMS Pages", FileEdit, false, "cms"], ["/admin/faqs", "FAQs", HelpCircle, false, "cms"], ["/admin/articles", "Advice", Newspaper, false, "cms"],
  ["/admin/complaints", "Complaints", MessageSquareWarning, false, "disputes"], ["/admin/paused-pros", "Paused Pros", PauseCircle, false, "disputes"], ["/admin/cases", "Trust & Support", AlertTriangle, false, "tickets"], ["/admin/audit", "Audit Log", ScrollText, false, "*"]];

const PERMS = { super_admin: ["*"], finance: ["finance", "transactions", "pricing", "overview"], verification: ["providers", "verification", "overview"], support: ["users", "disputes", "tickets", "reports", "jobs", "messages", "overview"], content: ["cms", "categories", "overview"] };

function AdminShell() {
  const { user } = useAuth();
  const p = PERMS[user?.staff_role] || [];
  return <DashLayout title="Admin" items={ADMIN_NAV.filter((n) => p.includes("*") || p.includes(n[4]))} />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/emergency" element={<SearchPage emergencyMode />} />
            <Route path="/professionals/:slug" element={<ProviderProfile />} />
            <Route path="/post-job" element={<PostJob />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:seo" element={<CategoryLanding />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/adverts" element={<AdvertsBrowse />} />
            <Route path="/urgent-jobs" element={<UrgentJobs />} />
            <Route path="/search-jobs" element={<SearchJobs />} />
            <Route path="/adverts/:slug" element={<AdvertDetail />} />
            <Route path="/homeowners" element={<CmsPage slug="homeowners" />} />
            <Route path="/for-professionals" element={<CmsPage slug="professionals" />} />
            <Route path="/trust-safety" element={<CmsPage slug="trust-safety" />} />
            <Route path="/legal/:slug" element={<CmsPage />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/advice" element={<Advice />} />
            <Route path="/cost-guides" element={<PriceGuideIndex />} />
            <Route path="/cost-guides/:slug" element={<PriceGuidePage />} />
            <Route path="/cost-guides/:slug/:town" element={<PriceGuidePage />} />
            <Route path="/advice/article/:slug" element={<Article />} />
            <Route path="/advice/:cat" element={<Advice />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/secure-account" element={<SecureAccount />} />
            <Route path="/reset-password" element={<Reset />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/review/:token" element={<LeaveReview />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/gift-vouchers" element={<GiftVouchers />} />
            <Route path="/guarantee" element={<GuaranteePage />} />
            <Route path="/report/:code" element={<TenantReport />} />
            <Route path="/gift-vouchers/success" element={<GiftVoucherSuccess />} />
            <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
            <Route path="/complaints/new" element={<Protected roles={["homeowner", "provider"]}><ComplaintForm /></Protected>} />
            <Route path="/:service/:town" element={<LocalSeo />} />
          </Route>
          <Route path="/dashboard" element={<Protected roles={["homeowner"]}><DashLayout title="Homeowner" items={HO_NAV} /></Protected>}>
            <Route index element={<HomeOverview />} />
            <Route path="jobs" element={<HomeJobs />} />
            <Route path="jobs/:id" element={<HomeJobDetail />} />
            <Route path="messages" element={<Messages />} />
            <Route path="saved" element={<HomeSaved />} />
            <Route path="bookings" element={<HomeBookings />} />
            <Route path="reviews" element={<HomeReviews />} />
            <Route path="reminders" element={<HomeReminders />} />
            <Route path="properties" element={<HomeProperties />} />
            <Route path="properties/:id" element={<HomePropertyDetail />} />
            <Route path="complaints" element={<MyComplaints />} />
            <Route path="complaints/:id" element={<ComplaintView />} />
            <Route path="account" element={<Account />} />
          </Route>
          <Route path="/provider" element={<Protected roles={["provider"]}><DashLayout title="Professional" items={PRO_NAV} extra={<PaymentModePrompt />} /></Protected>}>
            <Route index element={<ProOverview />} />
            <Route path="onboarding" element={<ProWelcome />} />
            <Route path="find-jobs" element={<ProFindJobs />} />
            <Route path="jobs/:id" element={<ProJob />} />
            <Route path="applications" element={<ProLeads />} />
            <Route path="adverts" element={<ProAdverts />} />
            <Route path="messages" element={<Messages />} />
            <Route path="my-jobs" element={<ProMyJobs />} />
            <Route path="calendar" element={<ProMyJobs calendar />} />
            <Route path="customers" element={<ProCustomers />} />
            <Route path="profile" element={<ProProfileEditor />} />
            <Route path="verification" element={<ProVerificationPage />} />
            <Route path="reviews" element={<ProReviews />} />
            <Route path="billing" element={<ProBilling />} />
            <Route path="analytics" element={<ProAnalytics />} />
            <Route path="complaints" element={<MyComplaints />} />
            <Route path="complaints/:id" element={<ComplaintView />} />
            <Route path="account" element={<Account />} />
          </Route>
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminShell /></Protected>}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="providers" element={<AdminProviders />} />
            <Route path="invites" element={<AdminInvites />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="adverts" element={<AdminCollection name="adverts" title="Service adverts" hint="Moderate provider adverts. Set status to active, paused or removed." label={(x) => `${x.title} · ${x.status}`} />} />
            <Route path="pricing" element={<AdminSettings />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="promotions" element={<AdminPromotions />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="categories" element={<AdminCategoriesPage />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="faqs" element={<AdminFaqs />} />
            <Route path="articles" element={<AdminArticles />} />
            <Route path="cases" element={<AdminCases />} />
            <Route path="complaints" element={<AdminComplaints />} />
            <Route path="complaints/:id" element={<AdminComplaintDetail />} />
            <Route path="paused-pros" element={<AdminPausedPros />} />
            <Route path="vouchers" element={<AdminVouchers />} />
            <Route path="verification" element={<AdminVerification />} />
            <Route path="price-guides" element={<AdminPriceGuides />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>
        </Routes>
        <TermsGate />
        <Toaster richColors position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}
